import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Chat, Send, Eye, Download, Trash } from "react-bootstrap-icons";
import { useForm, Controller } from "react-hook-form";
import FalconCloseButton from "components/common/FalconCloseButton";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { GetAllClaimPreAuthAllList } from "../config/preauthorizationApi";
import {
  CreateClaimCommunication,
  ViewClaim,
} from "../../cliams/config/cliamApi";
import {
  compressPDF,
  toBase64,
  compressImage,
  formatFileSize,
} from "../../utils";
import TextInput from "components/common/custom/forms/BootTextField/TextField";

const SendClaimCommunicationModal = ({
  modal,
  setModal,
  modalRow,
  parentCommunicationId = null,
}) => {
  const dispatch = useDispatch();
  const { viewClaimInfo } = useSelector((state) => state.claim);
  const { communcationDetails } = useSelector(
    (state) => state.preauthorization,
  );

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      communication_message: "",
      communication_category: "",
      communication_reason: "",
      request_priority: "",
      item: "",
      attachment: [],
    },
  });

  const items = useMemo(() => {
    return viewClaimInfo?.service_procedures || [];
  }, [viewClaimInfo?.service_procedures]);

  const { categories, priorities, reason } = useMemo(() => {
    const cats = [],
      prios = [],
      reason = [];
    communcationDetails?.forEach((item) => {
      if (item.cmnreqdtl_type === "category") {
        cats.push(item);
      } else if (item.cmnreqdtl_type === "priority") {
        prios.push(item);
      } else if (item.cmnreqdtl_type === "reasoncode") {
        reason.push(item);
      }
    });
    return { categories: cats, priorities: prios, reason };
  }, [communcationDetails]);

  const [files, setFiles] = useState([]);

  const [originalFileName, setOriginalFileName] = useState("");
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [compressionError, setCompressionError] = useState(null);
  // Calculate total file size

  useEffect(() => {
    if (modal && communcationDetails?.length === 0) {
      dispatch(GetAllClaimPreAuthAllList());
    }
  }, [modal, communcationDetails, dispatch]);

  useEffect(() => {
    const fetchPreauthData = async () => {
      if (!modalRow?.claimfull_id) return;
      try {
        await dispatch(
          ViewClaim({
            claimid: modalRow?.claimfull_id,
            claim_type: modalRow?.claim_type,
          }),
        ).unwrap();
      } catch (error) {
        console.error("Failed to fetch preauthorization data:", error);
      }
    };

    if (modal) {
      fetchPreauthData();
    } else {
      setOriginalFileName("");
      setCompressionProgress(0);
      setCompressionError(null);
    }
  }, [modal, modalRow, dispatch]);

  const handleFileChange = async (file, onChange) => {
    if (!file) {
      onChange(null);
      setOriginalFileName("");
      setCompressionProgress(0);
      setCompressionError(null);
      clearErrors("attachment");
      return;
    }

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setCompressionError("Only PDF or image files are allowed");
      toast.warning("Only PDF or image files are allowed");
      return;
    }

    setCompressionProgress(10);
    setCompressionError(null);

    try {
      let compressedFile = file;

      if (file.type === "application/pdf") {
        setCompressionProgress(40);
        compressedFile = await compressPDF(file);
        setCompressionProgress(80);
      } else if (file.type.startsWith("image/")) {
        compressedFile = await compressImage(file);
      }
      console.log({ compressedFile });
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (compressedFile.size > maxSize) {
        setCompressionError("Compressed file exceeds 10MB limit");
        toast.warning("Compressed file exceeds 10MB limit");
        setCompressionProgress(0);
        return;
      }

      const base64Data = await toBase64(compressedFile);

      const sanitizedName = file.name
        .replace(/\s+/g, "_")
        .replace(/\([^)]*\)/g, "")
        .replace(/[^\w.-]/g, "");
      const ext = sanitizedName
        .substring(sanitizedName.lastIndexOf("."))
        .toLowerCase();
      const hash = `${Date.now()}`;
      const title = `${hash}${ext}`;

      setOriginalFileName(file.name);

      onChange({
        contentType: compressedFile.type,
        title,
        originalName: file.name,
        size: compressedFile.size,
        creation: new Date().toISOString(),
        data: base64Data,
      });

      setCompressionProgress(100);
      clearErrors("attachment");
    } catch (error) {
      console.error("Compression error:", error);
      setCompressionError("Error processing file");
      toast.error("Error processing file");
      setCompressionProgress(0);
    }
  };

  const onSubmit = async (data) => {
    const payload = {
      claimid: modalRow?.claimfull_id,
      comun_itmseq: parseInt(data.item),
      comun_categ: data.communication_category,
      comun_cde: data.communication_reason,
      comun_prior: data.request_priority,
      comun_recvreqid: parentCommunicationId,
      comun_descp: data.communication_message,
      claim_type: modalRow?.claim_type,
    };

    if (data.attachment) {
      const attachment = Array.isArray(data.attachment)
        ? data.attachment[0]
        : data.attachment;
      if (attachment && attachment.data) {
        payload.attch = {
          contentType: attachment.contentType,
          title: attachment.title,
          creation: attachment.creation,
          data: attachment.data,
        };
      }
    }

    try {
      const response = await dispatch(
        CreateClaimCommunication(payload),
      ).unwrap();
      if (response?.success) {
        setModal(false);
        toast.success(response?.message_en);
        reset();
        setFiles([]);
      } else {
        toast.error(response?.message_en || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to send communication request");
    }
  };

  return (
    <Modal
      show={modal}
      centered
      onHide={() => {
        setModal(false);
        reset();
        setFiles([]);
      }}
      dialogClassName="modal-md"
    >
      <Modal.Header className="d-flex bg-primary text-white justify-content-between align-items-center ps-4 pe-4 pt-3">
        <Modal.Title as="h5" className=" fs-11">
          <Chat className="me-1" /> Send Communication Request
        </Modal.Title>
        <FalconCloseButton
          size="sm"
          onClick={() => {
            setModal(false);
            reset();
            setFiles([]);
          }}
          className="position-relative"
        />
      </Modal.Header>

      <Form>
        <Modal.Body>
          <Row>
            <Col xs={12} sm={6} xl={4}>
              <Controller
                name="communication_category"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="fs-11">
                      Communication Category
                    </Form.Label>
                    <Form.Select
                      {...field}
                      size="sm"
                      isInvalid={!!errors.communication_category}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option
                          key={cat.cmnreqdtl_code}
                          value={cat.cmnreqdtl_code}
                        >
                          {cat.cmnreqdtl_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.communication_category?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              />
            </Col>

            <Col xs={12} sm={6} xl={4}>
              <Controller
                name="communication_reason"
                control={control}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="fs-11">
                      Communication Reason
                    </Form.Label>
                    <Form.Select {...field} size="sm">
                      <option value="">Select Reason</option>
                      {reason.map((cat) => (
                        <option
                          key={cat.cmnreqdtl_code}
                          value={cat.cmnreqdtl_code}
                        >
                          {cat.cmnreqdtl_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
              />
            </Col>

            <Col xs={12} sm={6} xl={4}>
              <Controller
                name="request_priority"
                control={control}
                rules={{ required: "Priority is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="fs-11">Request Priority</Form.Label>
                    <Form.Select
                      {...field}
                      size="sm"
                      isInvalid={!!errors.request_priority}
                    >
                      <option value="">Select Priority</option>
                      {priorities.map((pri) => (
                        <option
                          key={pri.cmnreqdtl_code}
                          value={pri.cmnreqdtl_code}
                        >
                          {pri.cmnreqdtl_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.request_priority?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              />
            </Col>

            <Col xs={12} sm={12} md={6}>
              <Controller
                name="item"
                control={control}
                rules={{ required: "Item is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="fs-11">Item</Form.Label>
                    <Form.Select {...field} size="sm" isInvalid={!!errors.item}>
                      <option value="">Select Item</option>
                      {items.map((item, index) => {
                        const code = item?.itemcode || "N/A";
                        const display = item?.itemdescp || "";
                        return (
                          <option key={index + 1} value={index + 1}>
                            {`${code} - ${display}`}
                          </option>
                        );
                      })}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.item?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              />
            </Col>

            <Col xs={12} sm={12} md={6}>
              <Controller
                name="attachment"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <div>
                    <TextInput
                      name={field.name}
                      label="Attachment"
                      type="file"
                      control={control}
                      required={false}
                      rules={{ required: false }}
                      errors={errors}
                      onChange={(file) =>
                        handleFileChange(file, field.onChange)
                      }
                      value={
                        originalFileName ||
                        field.value?.originalName ||
                        "Choose file..."
                      }
                    />
                    {compressionProgress > 0 && compressionProgress < 100 && (
                      <div className="text-center mt-2">
                        <Spinner
                          animation="border"
                          variant="primary"
                          size="sm"
                        />
                        <ProgressBar
                          now={compressionProgress}
                          label={`${compressionProgress}%`}
                          className="mt-2"
                        />
                        <p className="mt-2 mb-0">Compressing file...</p>
                      </div>
                    )}
                    {compressionError && (
                      <div className="text-danger mt-2 fs-10">
                        {compressionError}
                      </div>
                    )}
                  </div>
                )}
              />
            </Col>

            <Col xs={12}>
              <Controller
                name="communication_message"
                control={control}
                rules={{ required: "Message is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="fs-11">
                      Communication Message
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      size="sm"
                      placeholder="Enter your message"
                      {...field}
                      isInvalid={!!errors.communication_message}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.communication_message?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              />
            </Col>

            {/* Display uploaded files */}
            {/* {files.length > 0 && (
              <Col xs={12}>
                <Form.Label className="fs-11 mt-2">Uploaded Files</Form.Label>
                <div className="d-flex flex-column gap-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="border rounded p-2 d-flex align-items-center justify-content-between"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted">
                          {file.type.includes("pdf") ? "📄" : "🖼️"}
                        </span>
                        <div>
                          <div className="fs-11 fw-semibold">
                            {file.title}
                          </div>
                          <small className="text-muted">
                            {formatFileSize(file.compressedFile?.size || 0)}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip style={{
                              "--bs-tooltip-bg": "var(--bs-warning)",
                              "--bs-tooltip-color": "var(--bs-white)",
                            }} id={`tooltip-view-${index}`} className="custom-tooltip">
                              View
                            </Tooltip>
                          }
                        >
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleViewFile(file)}
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: "32px", height: "32px" }}
                          >
                            <Eye size={16} />
                          </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip
                              style={{
                                "--bs-tooltip-bg": "var(--bs-warning)",
                                "--bs-tooltip-color": "var(--bs-white)",
                              }}
                              id={`tooltip-download-${index}`} className="rounded">
                              Download
                            </Tooltip>
                          }
                        >
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleDownloadFile(file)}
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: "32px", height: "32px" }}
                          >
                            <Download size={16} />
                          </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-remove-${index}`} className="custom-tooltip">
                              Remove
                            </Tooltip>
                          }
                        >
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveFile(index)}
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: "32px", height: "32px" }}
                          >
                            <Trash size={16} />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            )} */}
          </Row>
        </Modal.Body>

        <Modal.Footer className="border-top pt-2">
          <Button
            size="sm"
            variant="primary"
            className="fs-11"
            disabled={compressionProgress > 0 && compressionProgress < 100}
            onClick={handleSubmit(onSubmit)}
          >
            <Send className="me-1" /> Send
          </Button>
        </Modal.Footer>
      </Form>

      {/* Custom Tooltip Styles */}
      <style jsx="true">{`
        .custom-tooltip .tooltip-inner {
          background-color: #2c3e50 !important;
          color: #ffffff !important;
          font-size: 12px !important;
          padding: 6px 12px !important;
          border-radius: 4px !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
        }

        .custom-tooltip .tooltip-arrow::before {
          border-top-color: #2c3e50 !important;
        }

        .custom-tooltip.bs-tooltip-top .tooltip-arrow::before {
          border-top-color: #2c3e50 !important;
        }

        .custom-tooltip.bs-tooltip-bottom .tooltip-arrow::before {
          border-bottom-color: #2c3e50 !important;
        }

        .custom-tooltip.bs-tooltip-start .tooltip-arrow::before {
          border-left-color: #2c3e50 !important;
        }

        .custom-tooltip.bs-tooltip-end .tooltip-arrow::before {
          border-right-color: #2c3e50 !important;
        }
      `}</style>
    </Modal>
  );
};

export default SendClaimCommunicationModal;
